import { checkStorageApiCompliance, isStorageAvailable } from './utils'
import debug from '../console'
const disallowedKeys = ['getItem', 'setItem', 'removeItem', 'clear', 'length']

const logger = debug.withTag('StorageEmulator')

/**
 * A class that emulates the Storage API using an in-memory object.
 * If a custom storage object is provided and is compliant, it will use that instead.
 */
export class StorageEmulator implements Storage {
  private storage: Record<string, string> = {}

  /**
   * Creates an instance of StorageEmulator.
   * @param {Storage} [customStorage] - An optional custom storage object to use.
   */
  constructor(private customStorage?: Storage) {
    if (
      customStorage &&
      checkStorageApiCompliance(customStorage) &&
      isStorageAvailable(customStorage)
    ) {
      this.storage = customStorage
    } else {
      this.storage = {}
    }
  }

  /**
   * Sets a key-value pair in the storage.
   * @param {string} key - The key to set.
   * @param {string} value - The value to associate with the key.
   */
  setItem(key: string, value: string): void {
    if (disallowedKeys.includes(key)) {
      logger.warn(`Key "${key}" is not allowed in storage.`)
      return
    }
    this.storage[key] = value
  }

  /**
   * Retrieves the value associated with the given key.
   * @param {string} key - The key to retrieve.
   * @returns {string | null} The value associated with the key, or null if not found.
   */
  getItem(key: string): string | null {
    return this.storage[key] || null
  }

  /**
   * Removes the key-value pair associated with the given key.
   * @param {string} key - The key to remove.
   */
  removeItem(key: string): void {
    if (disallowedKeys.includes(key)) {
      logger.warn(`Key "${key}" is not allowed in storage.`)
      return
    }
    if (!this.storage[key]) {
      logger.warn(`Key "${key}" not found in storage.`)
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.storage[key]
  }

  /**
   * Clears all key-value pairs in the storage.
   */
  clear(): void {
    this.storage = {}
  }

  /**
   * Gets the number of key-value pairs in the storage.
   * @returns {number} The number of items in the storage.
   */
  get length(): number {
    return Object.keys(this.storage).length
  }

  /**
   * Retrieves the key at the specified index.
   * @param {number} index - The index of the key to retrieve.
   * @returns {string | null} The key at the specified index, or null if not found.
   */
  key(index: number): string | null {
    const keys = Object.keys(this.storage)
    return keys[index] || null
  }
}
